import React from 'react';
import './TaskIllustration.css';

type TaskType = 'audio' | 'photo' | 'video' | 'text' | 'screen' | 'survey' | 'choice' | 'signification' | 'signification-tension' | 'signification-triad' | 'signification-stones';

interface TaskIllustrationProps {
  type: TaskType;
  className?: string;
  /** Apply molecule-style floating animation (active card in carousel) */
  floating?: boolean;
}

export const taskTypeColors: Record<TaskType, string> = {
  audio:  '#D4715A',
  photo:  '#6E7A5A',
  video:  '#7BBFAF',
  text:   '#7A93B5',
  screen: '#C2AC8E',
  survey: '#B8A9C9',
  choice: '#C2889B',
  signification: '#7A93B5',
  'signification-tension': '#C2AC8E',
  'signification-triad': '#8B7EB5',
  'signification-stones': '#6E7A5A',
};

const illustrations: Record<TaskType, React.ReactNode> = {
  /* Audio — sound waves squiggling out from a node */
  audio: (
    <svg viewBox="-12 -16 60 50" fill="none">
      <circle cx="0" cy="0" r="6" fill="#fff"/>
      <path d="M8,0 Q12,-8 18,-6 Q24,-2 28,-10 Q34,-4 40,-8" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M5,5 Q10,12 16,10 Q22,16 28,12 Q34,20 42,14" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M-2,7 Q4,18 8,22 Q14,28 18,24" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  /* Photo — viewfinder frame with squiggly mountain + sun */
  photo: (
    <svg viewBox="-28 -28 56 56" fill="none">
      <circle cx="-18" cy="-18" r="5" fill="#fff"/>
      <circle cx="18" cy="-18" r="5" fill="#fff"/>
      <circle cx="18" cy="18" r="5" fill="#fff"/>
      <circle cx="-18" cy="18" r="5" fill="#fff"/>
      <line x1="-18" y1="-13" x2="-18" y2="13" stroke="#fff" strokeWidth="2"/>
      <line x1="18" y1="-13" x2="18" y2="13" stroke="#fff" strokeWidth="2"/>
      <line x1="-13" y1="-18" x2="13" y2="-18" stroke="#fff" strokeWidth="2"/>
      <line x1="-13" y1="18" x2="13" y2="18" stroke="#fff" strokeWidth="2"/>
      <path d="M-12,8 Q-6,-4 0,2 Q6,-8 12,4" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="-6" cy="-6" r="3" fill="#1a1814"/>
    </svg>
  ),
  /* Video — play button with squiggly film reels */
  video: (
    <svg viewBox="-28 -26 56 52" fill="none">
      <circle cx="-20" cy="0" r="5" fill="#fff"/>
      <circle cx="20" cy="0" r="5" fill="#fff"/>
      <path d="M-15,0 Q-10,-6 -4,-2 Q2,-8 8,-4 Q14,-10 15,0" fill="none" stroke="#fff" strokeWidth="2"/>
      <path d="M-15,0 Q-10,6 -4,2 Q2,8 8,4 Q14,10 15,0" fill="none" stroke="#fff" strokeWidth="2"/>
      <path d="M-6,-14 Q-2,-20 4,-16 Q10,-22 14,-14" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M-6,14 Q-2,20 4,16 Q10,22 14,14" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
      <polygon points="-4,-8 -4,8 8,0" fill="#1a1814"/>
    </svg>
  ),
  /* Text — squiggly handwriting lines */
  text: (
    <svg viewBox="-32 -30 70 44" fill="none">
      <circle cx="-24" cy="-20" r="5" fill="#fff"/>
      <path d="M-19,-20 Q-10,-26 0,-18 Q10,-28 20,-20 Q28,-26 32,-18" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M-24,-8 Q-14,-14 -4,-6 Q6,-16 16,-8 Q24,-14 28,-6" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M-24,6 Q-12,0 0,8 Q10,2 18,8" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  /* Screen — phone outline with squiggly tap ripple */
  screen: (
    <svg viewBox="-20 -32 48 64" fill="none">
      <circle cx="-12" cy="-24" r="4" fill="#fff"/>
      <circle cx="12" cy="-24" r="4" fill="#fff"/>
      <circle cx="12" cy="24" r="4" fill="#fff"/>
      <circle cx="-12" cy="24" r="4" fill="#fff"/>
      <path d="M-12,-20 Q-14,0 -12,20" fill="none" stroke="#fff" strokeWidth="2"/>
      <path d="M12,-20 Q14,0 12,20" fill="none" stroke="#fff" strokeWidth="2"/>
      <line x1="-8" y1="-24" x2="8" y2="-24" stroke="#fff" strokeWidth="2"/>
      <line x1="-8" y1="24" x2="8" y2="24" stroke="#fff" strokeWidth="2"/>
      <circle cx="0" cy="2" r="6" fill="#1a1814"/>
      <path d="M8,8 Q14,14 12,20 Q18,24 22,18" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M14,4 Q20,8 18,14" fill="none" stroke="#1a1814" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  /* Choice — central node with 4 squiggly waves radiating outward */
  choice: (
    <svg viewBox="-28 -28 56 56" fill="none">
      <circle cx="0" cy="0" r="6" fill="#fff"/>
      {/* Top-right wave */}
      <path d="M5,-4 Q10,-12 16,-8 Q22,-14 28,-10" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      {/* Bottom-right wave */}
      <path d="M5,4 Q12,10 16,6 Q22,14 28,10" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      {/* Top-left wave */}
      <path d="M-5,-4 Q-12,-12 -18,-6 Q-24,-14 -28,-8" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      {/* Bottom-left wave */}
      <path d="M-4,5 Q-10,14 -16,10 Q-22,18 -26,14" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  /* Signification — triangle with tension line and inner dot */
  signification: (
    <svg viewBox="-30 -28 60 56" fill="none">
      {/* Equilateral triangle — hand-drawn style */}
      <path d="M0,-22 Q-2,-20 -24,18 Q-22,20 24,18 Q22,16 0,-22" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Inner dot — placement marker */}
      <circle cx="0" cy="4" r="5" fill="#1a1814"/>
      {/* Squiggly connection lines to vertices */}
      <path d="M0,-1 Q-4,-12 0,-18" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      <path d="M-3,7 Q-14,14 -20,16" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      <path d="M3,7 Q14,14 20,16" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      {/* Vertex nodes */}
      <circle cx="0" cy="-22" r="4" fill="#fff"/>
      <circle cx="-24" cy="18" r="4" fill="#fff"/>
      <circle cx="24" cy="18" r="4" fill="#fff"/>
    </svg>
  ),
  /* Signification: Tension — horizontal slider track with handle dot */
  'signification-tension': (
    <svg viewBox="-14 -20 68 48" fill="none">
      {/* Track line — squiggly */}
      <path d="M-4,4 Q4,1 14,5 Q22,2 30,4 Q38,7 46,4" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      {/* Left pole node */}
      <circle cx="-4" cy="4" r="6" fill="#fff"/>
      {/* Right pole node */}
      <circle cx="46" cy="4" r="6" fill="#fff"/>
      {/* Handle dot — off-center */}
      <circle cx="28" cy="4" r="7" fill="#1a1814"/>
      {/* Squiggly emanation waves from handle */}
      <path d="M28,-5 Q33,-14 38,-8" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M28,13 Q33,22 38,16" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  ),
  /* Signification: Triad — same as generic signification (triangle) */
  'signification-triad': (
    <svg viewBox="-30 -28 60 56" fill="none">
      <path d="M0,-22 Q-2,-20 -24,18 Q-22,20 24,18 Q22,16 0,-22" fill="none" stroke="#1a1814" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="0" cy="4" r="5" fill="#1a1814"/>
      <path d="M0,-1 Q-4,-12 0,-18" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      <path d="M-3,7 Q-14,14 -20,16" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      <path d="M3,7 Q14,14 20,16" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="0" cy="-22" r="4" fill="#fff"/>
      <circle cx="-24" cy="18" r="4" fill="#fff"/>
      <circle cx="24" cy="18" r="4" fill="#fff"/>
    </svg>
  ),
  /* Signification: Stones — grouped dots with connecting lines */
  /* Signification: Stones — grouped dots with connecting lines */
  'signification-stones': (
    <svg viewBox="-24 -18 80 54" fill="none">
      {/* Cluster left — 3 dots grouped close */}
      <circle cx="2" cy="0" r="6" fill="#fff"/>
      <circle cx="14" cy="-10" r="5" fill="#fff"/>
      <circle cx="6" cy="12" r="5" fill="#1a1814"/>
      {/* Lines joining the cluster */}
      <line x1="2" y1="0" x2="14" y2="-10" stroke="#fff" strokeWidth="2.5"/>
      <line x1="2" y1="0" x2="6" y2="12" stroke="#fff" strokeWidth="2.5"/>

      {/* Cluster right — 2 dots grouped */}
      <circle cx="34" cy="4" r="6" fill="#1a1814"/>
      <circle cx="24" cy="16" r="5" fill="#fff"/>
      {/* Line joining */}
      <line x1="34" y1="4" x2="24" y2="16" stroke="#fff" strokeWidth="2.5"/>

      {/* Squiggly waves — 2 full waves each */}
      <path d="M-4,16 Q-10,24 -16,18 Q-22,12 -28,18" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M38,10 Q44,18 50,12 Q56,18 62,12" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  ),
  /* Survey — checkmarks with squiggly connectors */
  survey: (
    <svg viewBox="-30 -26 58 58" fill="none">
      <circle cx="-20" cy="-16" r="5" fill="#fff"/>
      <circle cx="-20" cy="4" r="5" fill="#fff"/>
      <circle cx="-20" cy="24" r="5" fill="#fff"/>
      <path d="M-15,-16 Q-8,-22 0,-14 Q8,-20 16,-12" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M-15,4 Q-6,-2 4,6 Q12,0 20,8" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M-15,24 Q-4,18 6,26 Q14,20 22,28" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
      <path d="M-24,-12 Q-26,0 -24,0" fill="none" stroke="#fff" strokeWidth="2"/>
      <path d="M-24,8 Q-26,16 -24,20" fill="none" stroke="#fff" strokeWidth="2"/>
    </svg>
  ),
};

export const TaskIllustration: React.FC<TaskIllustrationProps> = ({
  type,
  className = '',
  floating = false,
}) => {
  const bg = taskTypeColors[type] || taskTypeColors.text;
  const svg = illustrations[type] || illustrations.text;

  return (
    <div
      className={`tc-illus ${floating ? 'tc-illus--float' : ''} ${className}`}
      style={{ backgroundColor: bg }}
    >
      {svg}
    </div>
  );
};
